'use client';

import { useMemo, useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

// Indent well-formed XML using only string transforms (no DOM), so the pretty
// output survives self-closing tags and inline text nodes.
function formatXml(xml: string): string {
  const withBreaks = xml
    .replace(/\r\n?/g, '\n')
    .replace(/>\s*</g, '>\n<')
    .replace(/^\s+|\s+$/g, '');
  const lines = withBreaks.split('\n');
  let pad = 0;
  const indent = '  ';
  const out: string[] = [];

  for (const raw of lines) {
    const node = raw.trim();
    if (!node) {
      continue;
    }
    const isClosing = /^<\//.test(node);
    const isSelfContained =
      /^<[^!?][^>]*\/>$/.test(node) || /^<([\w:-]+)[^>]*>.*<\/\1>$/.test(node);
    const isOpening = /^<[^!?/][^>]*[^/]>$/.test(node) && !isSelfContained;
    const isDeclaration = /^<[!?]/.test(node);

    if (isClosing && pad > 0) {
      pad -= 1;
    }

    out.push(indent.repeat(pad) + node);

    if (isOpening && !isDeclaration) {
      pad += 1;
    }
  }

  return out.join('\n');
}

type ValidationResult =
  | { state: 'empty' }
  | { state: 'valid'; formatted: string }
  | { state: 'error'; message: string };

function validateXml(input: string): ValidationResult {
  if (!input.trim()) {
    return { state: 'empty' };
  }
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return { state: 'empty' };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(input, 'application/xml');
  const errorNode = doc.querySelector('parsererror');

  if (errorNode) {
    const raw = (errorNode.textContent ?? 'The XML is not well-formed.')
      .replace(/\s+/g, ' ')
      .trim();
    return { state: 'error', message: raw };
  }

  try {
    return { state: 'valid', formatted: formatXml(input) };
  } catch {
    return { state: 'valid', formatted: input.trim() };
  }
}

const SAMPLE_XML = `<note>
  <to>Team</to>
  <from>Cleanor</from>
  <heading>Reminder</heading>
  <body>Validate before you ship.</body>
</note>`;

export default function BrowserXmlValidator() {
  const [input, setInput] = useState<string>(SAMPLE_XML);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const result = useMemo(() => validateXml(input), [input]);
  const formatted = result.state === 'valid' ? result.formatted : '';

  async function handleCopy() {
    try {
      await copyText(formatted);
      setCopyMessage('Copied formatted XML to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Client-side</span>
        <h2 className="tool-live-title">XML validator</h2>
        <p className="tool-live-subtitle">
          Paste XML to check whether it is well-formed. See the first parse error or a formatted
          version. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>XML input</span>
              <textarea
                onChange={(event) => setInput(event.target.value)}
                rows={16}
                value={input}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={result.state !== 'valid'}
              onClick={handleCopy}
              type="button"
            >
              Copy formatted
            </button>
          </div>
          {result.state === 'error' ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              Not well-formed: {result.message}
            </p>
          ) : null}
          {result.state === 'valid' ? (
            <p className="status-message status-success">This XML is well-formed.</p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Formatted XML</span>
            <textarea readOnly rows={16} value={result.state === 'valid' ? formatted : ''} />
          </label>
          <p className="tool-live-panel-note">
            Checked locally in your browser with the built-in XML parser.
          </p>
        </section>
      </div>
    </div>
  );
}
